import { Button } from '@mm/components';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import AudiencesModel, {
  FilterItem,
  FilterParams,
  FILTER_TYPES,
  SystemFilterItem,
  TagFilterItem,
  TagsFilters,
  FilterItemOperatorTypes,
} from '@mm/state/model/AudiencesModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { getUniqId } from '@mm/utils';
import { Menu, MenuItem } from '@szhsin/react-menu';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon } from '@mm/icons/ComponentIcons';
import { OnClickElsewhere } from '../../../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import { colors } from '../../../styles/colors';
import styles from './FilterBar.module.css';
import { FilterBarItem } from './FilterBarItem';
import classNames from 'classnames';
import { CloseOutlined } from '@ant-design/icons';
import { selectAudienceVariables } from '@mm/state/selectors/audienceSelector';
import { convertToUpperSnakeCase } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { Spin } from 'antd';
import {
  MyLeadsFilterTypes,
  MY_LEADS_OPERATORS_BY_TYPE,
} from '@mm/state/interfaces/myLeads';

interface Props {
  activeAudience?: AudiencesModel;
  onFiltersUpdated: (
    filterParams: FilterParams | undefined,
    tagsFilters: TagsFilters | undefined,
    esFilters: any | undefined,
  ) => void;
  totalFound?: number;
  css?: {
    filterBarItem?: string;
    addFilterButton?: string;
    filterBarItemComponent?: string;
    filterBarItemDeleteContainer?: string;
    filterBarItemDeleteButton?: string;
  };
  plusIconColor?: string;
  withReset?: boolean;
}
interface FilterEditAttributes {
  dirty: boolean;
  uniqId: string;
  deleted?: boolean;
  new?: boolean;
}

type PendingFilter =
  | ({ owner: 'filterParams' } & (FilterItem | SystemFilterItem) &
      FilterEditAttributes)
  | ({ owner: 'tagsFilters' } & TagFilterItem & FilterEditAttributes)
  | ({ owner: 'esFilters' } & Partial<FilterItem> & FilterEditAttributes);

export const FilterBar = (props: Props) => {
  const {
    activeAudience,
    onFiltersUpdated,
    totalFound,
    css,
    plusIconColor,
    withReset,
  } = props;
  const { id: activeAudienceId } = activeAudience || {};
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const hasFilters =
    Object.keys(activeAudience?.filterParams || {}).length ||
    Object.keys(activeAudience?.esFilters || {}).length ||
    Object.keys(activeAudience?.tagsFilters || {}).length;
  const audienceVariables = useSelector(selectAudienceVariables);

  const isPurchasedAudience = activeAudience?.type === 'leads_db';
  const esFilterWithoutVarId = (filter: PendingFilter) =>
    filter.owner === 'esFilters' &&
    !filter.variableId &&
    filter.filterType !== FILTER_TYPES.platforms;
  const hasServiceTag = (filter: PendingFilter) =>
    filter.owner === 'tagsFilters' &&
    filter.values?.some(v => v.kind === 'service');

  const [pendingFilters, setPendingFilters] = useState<PendingFilter[]>([]);
  const [filterUnderEdit, setFilterUnderEdit] = useState<{
    isNew?: boolean;
    uniqId: string;
  }>();
  const [busy, setBusy] = useState(false);

  const mapVariables = (
    variable: [string, any[]] | object,
    isExcluded?: boolean,
    esType?: MyLeadsFilterTypes,
  ) => {
    const variableUpperCase = convertToUpperSnakeCase(variable[0]);
    const audienceVariable = audienceVariables.find(
      v => v.name === variableUpperCase,
    );
    const isPlatformsFilter = variable[0] === 'platform';

    const operator = variable[1].some(
      v => typeof v === 'object' && (v?.gt || v?.lt),
    )
      ? undefined
      : esType
      ? MY_LEADS_OPERATORS_BY_TYPE[esType]
      : 'equal';

    return {
      owner: 'esFilters',
      filterType: isPlatformsFilter
        ? FILTER_TYPES.platforms
        : FILTER_TYPES.variableValues,
      dirty: false,
      uniqId: getUniqId('esFilters'),
      variableId: audienceVariable?.variableId,
      variableType: audienceVariable?.variableType,
      variable: variable[0],
      value: variable[1],
      ...(!isPlatformsFilter && { operator }),
      valid: true,
      excluded: isExcluded,
      esType,
    };
  };

  const onCancelEdit = () => {
    if (filterUnderEdit?.isNew) {
      setPendingFilters(filters =>
        filters.filter(({ uniqId }) => uniqId !== filterUnderEdit.uniqId),
      );
    }
    setFilterUnderEdit(undefined);
  };
  const onAddFilter = (type: 'attributes' | 'platform' | 'tags') => {
    onCancelEdit();
    const uniqId = getUniqId('filter');

    if (type === 'tags') {
      setPendingFilters(pendingFilters => [
        ...pendingFilters,
        {
          owner: 'tagsFilters',
          filterType: FILTER_TYPES.tagsFilters,
          values: [],
          operator: 'include',
          dirty: true,
          new: true,
          uniqId,
        },
      ]);
    } else if (type === 'platform') {
      setPendingFilters(pendingFilters => [
        ...pendingFilters,
        {
          owner: 'esFilters',
          filterType: FILTER_TYPES.platforms,
          variable: 'platform',
          value: [],
          esType: MyLeadsFilterTypes.attributes,
          dirty: true,
          new: true,
          uniqId,
        },
      ]);
    } else {
      setPendingFilters(pendingFilters => [
        ...pendingFilters,
        {
          owner: 'filterParams',
          filterType: FILTER_TYPES.variableValues,
          value: [],
          dirty: true,
          new: true,
          uniqId,
        },
      ]);
    }
    setFilterUnderEdit({ uniqId, isNew: true });
  };
  const onUpdateFilter = (uniqId: string) => updatedFilter => {
    const variable = updatedFilter?.variableId
      ? audienceVariables.find(v => v.variableId === updatedFilter?.variableId)
      : null;

    const filter = !updatedFilter
      ? undefined
      : variable?.esType
      ? { ...updatedFilter, owner: 'esFilters' }
      : { ...updatedFilter, owner: updatedFilter.owner };

    setPendingFilters(filters =>
      filters
        .map(oldFilter => {
          if (oldFilter.uniqId !== uniqId) {
            return oldFilter;
          }
          // undefined means delete this filter
          if (filter) {
            return { ...filter, dirty: true };
          }

          return oldFilter.new ? null : { deleted: true, dirty: true };
        })
        .filter(Boolean),
    );
    setFilterUnderEdit(undefined);
  };

  const onResetAllFilters = () => {
    setPendingFilters(filters =>
      filters.map(filter => ({ ...filter, dirty: true, deleted: true })),
    );
  };

  const esFilterToValue = (filter: FilterItem): any[] => {
    let value;
    if (filter.operator === FilterItemOperatorTypes.GREATER_THAN) {
      value = [{ gt: filter.value }];
    } else if (filter.operator === FilterItemOperatorTypes.LESS_THAN) {
      value = [{ lt: filter.value }];
    } else {
      value = filter.value;
    }

    return value;
  };

  const getESFilterStructure = filters => {
    const result = {};
    filters.forEach(filter => {
      if (filter.esType) {
        if (!result[filter.esType]) {
          result[filter.esType] = {};
        }
        if (!result[filter.esType][filter.variable]) {
          result[filter.esType][filter.variable] = [];
        }
        const value = esFilterToValue(filter);

        result[filter.esType][filter.variable].push(...value);
      }
    });

    return result;
  };

  useEffect(() => {
    if (!filterUnderEdit) {
      const somethingChanged = pendingFilters.some(fp => fp.dirty);
      if (!somethingChanged) {
        onFiltersUpdated(undefined, undefined, undefined);
        return;
      }
      // Strip off local attributes that shouldn't be sent to the BE
      const cleanupFilter = ({
        dirty: _dirty,
        owner: _owner,
        new: _new = false,
        esType: _esType = null as any,
        ...filter
      }) => ({
        ...filter,
      });

      const filterParams = pendingFilters
        .filter(f => !f.deleted && f.owner === 'filterParams')
        .map(cleanupFilter);
      const tagsFilters = pendingFilters
        .filter(f => !f.deleted && f.owner === 'tagsFilters')
        .map(cleanupFilter);
      const esFilters = pendingFilters.filter(
        f => !f.deleted && f.owner === 'esFilters',
      );

      onFiltersUpdated(
        {
          ...(filterParams.length ? { logic: '1' } : {}),
          ...filterParams.reduce((acc, filter, i) => {
            acc[i + 1] = filter;
            return acc;
          }, {}),
        },
        tagsFilters.reduce((acc, filter, i) => {
          acc[i + 1] = filter;
          return acc;
        }, {}),
        getESFilterStructure(esFilters),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUnderEdit, pendingFilters]);
  useEffect(() => {
    (async () => {
      setFilterUnderEdit(undefined);
      if (!activeAudienceId) {
        setPendingFilters([]);
        return;
      }
      setBusy(true);
      const audience = await audiencesCRUDActions.retrieveAudienceById(
        activeChatbotId,
        activeAudienceId,
        true,
        { skipBusy: true },
      );

      setPendingFilters([
        ...Object.entries(audience?.filterParams || {})
          .filter(([k, _v]) => k !== 'logic')
          .map(([_k, v]) => ({
            ...v,
            owner: 'filterParams',
            dirty: false,
            uniqId: getUniqId('filterParams'),
          })),

        ...Object.entries(audience?.esFilters || {}).map(([key, value]) =>
          mapVariables(
            Object.entries(value || {}).flatMap(i => i),
            MyLeadsFilterTypes.excludedPrefixVariables ===
              MyLeadsFilterTypes[key],
            MyLeadsFilterTypes[key],
          ),
        ),

        ...Object.values(audience?.tagsFilters || {}).map(f => ({
          ...f,
          owner: 'tagsFilters',
          dirty: false,
          uniqId: getUniqId('tagsFilters'),
        })),
      ]);
      setBusy(false);
    })();
  }, [activeAudienceId, activeChatbotId, audiencesCRUDActions]);

  return (
    <OnClickElsewhere
      handleClick={onCancelEdit}
      className={classNames(styles.container, {
        [styles.containerWithReset]: withReset,
      })}
    >
      {withReset && hasFilters && !isPurchasedAudience ? (
        <div className={styles.reset} onClick={onResetAllFilters}>
          Reset filters <CloseOutlined />
        </div>
      ) : null}
      <Spin spinning={busy} size="small">
        <div className={classNames(styles.filterBar)}>
          {!busy &&
            pendingFilters
              .filter(f => f && !f.deleted)
              .map(filter => (
                <FilterBarItem
                  key={filter.uniqId}
                  filter={filter}
                  onUpdate={onUpdateFilter(filter.uniqId)}
                  onStartEdit={() => {
                    if (
                      isPurchasedAudience ||
                      esFilterWithoutVarId(filter) ||
                      hasServiceTag(filter)
                    ) {
                      return;
                    }
                    onCancelEdit();
                    setFilterUnderEdit({ uniqId: filter.uniqId });
                  }}
                  onCancelEdit={onCancelEdit}
                  editing={filterUnderEdit?.uniqId === filter.uniqId}
                  className={css?.filterBarItem}
                  css={{
                    component: css?.filterBarItemComponent,
                    deleteContainer: css?.filterBarItemDeleteContainer,
                    deleteButton: css?.filterBarItemDeleteButton,
                  }}
                  hideDelete={isPurchasedAudience}
                />
              ))}

          {!isPurchasedAudience && (
            <div>
              <Menu
                menuButton={
                  <Button
                    css={{ container: css?.addFilterButton }}
                    color="link"
                    leftIcon={
                      <PlusIcon
                        color={plusIconColor || colors.accentBlue}
                        size={16}
                      />
                    }
                  >
                    Add filter
                  </Button>
                }
              >
                <MenuItem
                  onClick={() => onAddFilter('platform')}
                  disabled={pendingFilters.some(
                    f => f.filterType === 'platforms',
                  )}
                >
                  Platform
                </MenuItem>
                <MenuItem onClick={() => onAddFilter('tags')}>Tags</MenuItem>
                <MenuItem onClick={() => onAddFilter('attributes')}>
                  Attributes
                </MenuItem>
              </Menu>
            </div>
          )}
        </div>
      </Spin>
      {totalFound !== undefined && (
        <div className={styles.total}>
          <span className={styles.totalCount}>{totalFound}</span> contacts
        </div>
      )}
    </OnClickElsewhere>
  );
};
